import { version } from '../../../../package.json';

export const portalNeoVersionUDI = () => {
  return version
    .split('.')
    .map((part) => part.padStart(2, '0'))
    .join('');
};

/**
 * Formats a raw UDI string into GS1 standard format
 * @param rawUDI - The raw UDI string from the algorithm
 * @returns Formatted UDI with brackets following GS1 standard
 */
export const formatUDI = (rawUDI: string): string => {
  if (!rawUDI) return '';

  // Check if already formatted (algorithm 2.0.0+ will send it pre-formatted)
  if (rawUDI.includes('(01)') && rawUDI.includes('(8012)')) {
    return `${rawUDI}.${portalNeoVersionUDI()}`;
  }

  try {
    // Handle algorithm 1.23.2 format: "01840193400269211232"
    if (rawUDI.startsWith('01') && rawUDI.length === 20) {
      const gtin = rawUDI.substring(2, 16); // Characters 3-16 (GTIN)
      const versionPart = rawUDI.substring(16); // Last 4 characters

      // Transform "1232" → "012302"
      if (versionPart.length === 4) {
        const major = versionPart[0];
        const minor = versionPart.substring(1, 3);
        const patch = versionPart[3];
        const formattedVersion = `0${major}${minor}0${patch}`;
        return `(01)${gtin}(8012)${formattedVersion}.${portalNeoVersionUDI()}`;
      }
    }

    // Fallback for other formats or future algorithm versions
    return rawUDI;
  } catch (error) {
    console.warn('Error formatting UDI:', error);
    return rawUDI;
  }
};

/**
 * Validates if a UDI string is in the correct GS1 format
 * @param udi - The UDI string to validate
 * @returns True if the UDI is in the correct format
 */
export const isValidUDIFormat = (udi: string): boolean => {
  if (!udi) return false;

  // Check for GS1 format with Application Identifiers
  // Simple validation: starts with (01) and has minimum length
  return udi.startsWith('(01)') && udi.length >= 18;
};

/**
 * Gets the GTIN (Device Identifier) part from a formatted UDI
 * @param udi - The formatted UDI string
 * @returns The GTIN part or empty string if not found
 */
export const extractGTIN = (udi: string): string => {
  const match = /\(01\)(\d{14})/.exec(udi);
  return match ? match[1] : '';
};
